import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, ClipboardCheck, GraduationCap, Phone,
  Sparkles, CheckCircle2, Calendar, FileText, Award,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import {
  PFLICHT_THEMEN,
  PFLICHT_REZERT,
  PFLICHT_RAHMEN,
} from "@/data/pflichtfortbildungen";

export default function Pflichtfortbildungen() {
  useEffect(() => {
    document.title =
      "Pflichtfortbildungen für Pflegeeinrichtungen · MD-prüfungsfest | Dickies-Campus";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.name = "description";
      document.head.appendChild(m);
    }
    m.content =
      "Alle Pflichtfortbildungen für Pflegedienste & Heime auf einen Blick: Hygiene, Datenschutz, Erste Hilfe, Brandschutz, Rezertifizierungen §43b und §4 PflAPrV. Online, jeden Montag startklar.";
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />

      <main>
        {/* HERO · light Peach mit Rosa-Blobs + Spec-Card rechts */}
        <section className="relative pt-36 lg:pt-44 pb-16 lg:pb-24 bg-peach-50 overflow-hidden border-b border-cream-300">
          {/* Dekorative Schichten (weich, ohne Schatten) */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-rose/25 blur-3xl" />
            <div className="absolute -bottom-40 right-0 w-[34rem] h-[34rem] rounded-full bg-peach-200/40 blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
              {/* LEFT · Story */}
              <Reveal>
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-px w-12 bg-terra" />
                    <span className="text-[11px] uppercase tracking-[0.32em] font-bold text-terra">
                      Für Träger &amp; Einrichtungen
                    </span>
                  </div>

                  <h1
                    data-testid="pflicht-page-h1"
                    className="font-display text-sand-800 tracking-tight leading-[1.04] text-[2.4rem] sm:text-5xl lg:text-[3.6rem] font-semibold"
                  >
                    Pflichtfortbildungen.<br />
                    <span className="text-terra italic">Sauber dokumentiert.</span>
                  </h1>

                  <p className="mt-7 text-sand-700 text-[16.5px] sm:text-[17px] leading-[1.7] max-w-xl">
                    Pflegekassen und MD erwarten pro Mitarbeitendem jährlich rund{" "}
                    <b className="text-sand-800">{PFLICHT_RAHMEN.basisStunden} Fortbildungsstunden</b>.
                    Praxisanleitende benötigen zusätzlich{" "}
                    <b className="text-sand-800">{PFLICHT_RAHMEN.praxisanleiterStunden} Stunden</b>{" "}
                    pro Jahr. Wir bieten alle relevanten Themen — als Einzelkurs oder
                    Jahres-Paket für dein gesamtes Team.
                  </p>

                  {/* USP-Pills */}
                  <ul className="mt-7 grid grid-cols-2 gap-2.5 max-w-md">
                    {[
                      { icon: Calendar,      k: "Jeden Montag", d: "neuer Kursstart" },
                      { icon: FileText,      k: "MD-konform",   d: "UE + FP nachweisbar" },
                      { icon: Award,         k: "Zertifikat",   d: "direkt zur Personalakte" },
                      { icon: ShieldCheck,   k: "11 Themen",    d: "ein Anbieter" },
                    ].map(({ icon: Icon, k, d }) => (
                      <li key={k} className="flex items-center gap-3 rounded-2xl bg-white border border-cream-300 px-3.5 py-2.5">
                        <Icon className="h-4 w-4 text-terra shrink-0" strokeWidth={1.8} />
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-semibold text-sand-800 leading-tight">{k}</div>
                          <div className="text-[10.5px] uppercase tracking-[0.18em] text-sand-500 mt-0.5">{d}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-9 flex flex-wrap items-center gap-4">
                    <Button
                      asChild
                      data-testid="pflicht-page-cta-primary"
                      className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[14.5px] font-semibold"
                    >
                      <Link to="/pflegedienste#pakete">
                        Jahres-Fortbildungsplan ansehen &amp; buchen
                        <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
                      </Link>
                    </Button>
                    <a
                      href="tel:+4921129273330"
                      data-testid="pflicht-page-cta-phone"
                      className="inline-flex items-center gap-2 text-[14.5px] font-medium text-brand-darkred underline-offset-4 hover:underline"
                    >
                      <Phone className="h-4 w-4" strokeWidth={1.8} />
                      0211 29273330
                    </a>
                  </div>
                </div>
              </Reveal>

              {/* RIGHT · Spec-Card */}
              <Reveal delay={80}>
                <div className="relative">
                  <div className="absolute -top-4 left-8 z-10 inline-flex items-center gap-2 rounded-full bg-terra text-white px-4 h-8 text-[10.5px] uppercase tracking-[0.24em] font-bold">
                    <Sparkles className="h-3 w-3" strokeWidth={2.2} /> Pflicht-Pflichten 2026
                  </div>
                  <div className="rounded-3xl bg-white text-sand-800 p-8 sm:p-10 border border-cream-300">
                    <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-terra">
                      Pflicht-Quoten
                    </div>
                    <h3 className="mt-3 font-display text-2xl sm:text-[1.85rem] text-sand-800 leading-tight">
                      Was Pflegedienste<br />
                      <span className="italic text-terra">jedes Jahr nachweisen müssen.</span>
                    </h3>

                    <dl className="mt-7 divide-y divide-cream-300">
                      {[
                        ["Fortbildungsstunden / Person", `${PFLICHT_RAHMEN.basisStunden} Std`],
                        ["Praxisanleitung (§ 4 PflAPrV)", `+${PFLICHT_RAHMEN.praxisanleiterStunden} Std`],
                        ["1 Fortbildungspunkt", PFLICHT_RAHMEN.fpRegel],
                        ["Online-Kurs-Format", "Live + Selbststudium"],
                        ["Themen im Katalog", `${PFLICHT_THEMEN.length}`],
                        ["Rezertifizierungen", `${PFLICHT_REZERT.length} (§ 43b · § 4 PflAPrV)`],
                        ["Zertifikat", "PDF · sofort nach Abschluss"],
                        ["MD-Audit-tauglich", "ja · UE + FP ausgewiesen"],
                      ].map(([k, v]) => (
                        <div key={k} className="flex items-baseline justify-between gap-4 py-3">
                          <dt className="text-[12px] uppercase tracking-[0.18em] text-sand-500 font-semibold">{k}</dt>
                          <dd className="text-[13.5px] text-sand-800 font-medium text-right">{v}</dd>
                        </div>
                      ))}
                    </dl>

                    <div className="mt-6 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-terra text-white px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2.2} /> Online
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-rose/35 text-brand-darkred px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                        Jeden Montag
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-peach-100 text-sand-700 px-3 h-7 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                        MD-prüfungsfest
                      </span>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* RAHMEN · 3 Pillar-Karten */}
        <section className="bg-cream-50 py-16 lg:py-20 border-b border-cream-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Der gesetzliche Rahmen
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-[2.4rem] text-sand-800 tracking-tight leading-[1.1] max-w-3xl">
                Drei Zahlen, <span className="text-terra italic">die jeder PDL kennen muss.</span>
              </h2>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {[
                {
                  icon: ClipboardCheck,
                  k: `${PFLICHT_RAHMEN.basisStunden} Std / Jahr`,
                  t: "Pro Pflegemitarbeiter:in",
                  d: "Empfehlung für jede:n Pflegemitarbeiter:in zur MD-Prüfungssicherheit. Wer das nicht nachweisen kann, riskiert Abwertung in der Prüfung.",
                },
                {
                  icon: GraduationCap,
                  k: `+${PFLICHT_RAHMEN.praxisanleiterStunden} Std / Jahr`,
                  t: "Für Praxisanleitung",
                  d: "Für Praxisanleiter:innen nach § 4 PflAPrV verbindlich vorgeschrieben — zusätzlich zur Basis-Fortbildung.",
                },
                {
                  icon: ShieldCheck,
                  k: PFLICHT_RAHMEN.fpRegel,
                  t: "FP-Regel",
                  d: "Zertifikate mit nachvollziehbarer UE- und FP-Angabe — direkt für die Personalakte und MD-Audit-tauglich.",
                },
              ].map(({ icon: Icon, k, t, d }) => (
                <Reveal key={k}>
                  <div className="rounded-3xl bg-white border border-cream-300 p-7 h-full hover:border-terra transition-colors">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-rose/25 text-brand-darkred">
                      <Icon className="h-5 w-5" strokeWidth={1.8} />
                    </span>
                    <div className="mt-5 font-display text-2xl text-sand-800 leading-tight">{k}</div>
                    <div className="mt-1 text-[10.5px] uppercase tracking-[0.22em] font-bold text-terra">{t}</div>
                    <p className="mt-3 text-sand-700 text-[14px] leading-relaxed">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* THEMEN-GRID */}
        <section className="py-20 bg-peach-50 border-b border-cream-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="mb-12 flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-px w-10 bg-terra" />
                    <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                      Alle Pflichtthemen
                    </span>
                  </div>
                  <h2 className="font-display text-sand-800 text-3xl sm:text-[2.4rem] font-semibold tracking-tight leading-[1.1]">
                    {PFLICHT_THEMEN.length} Themen. <span className="text-terra italic">Ein Anbieter.</span>
                  </h2>
                </div>
                <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white border border-cream-300 px-4 h-9 text-[11.5px] font-semibold text-sand-700">
                  <Sparkles className="h-3.5 w-3.5 text-terra" strokeWidth={2} />
                  Alle Themen einzeln oder im Jahres-Paket
                </div>
              </div>
            </Reveal>

            <div
              data-testid="pflicht-page-themen-grid"
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {PFLICHT_THEMEN.map((t, i) => (
                <Reveal key={t.slug} delay={i * 30}>
                  <div
                    data-testid={`pflicht-page-theme-${t.slug}`}
                    className="group bg-white border border-cream-300 rounded-3xl p-6 hover:border-terra transition-colors h-full flex flex-col"
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-3">
                      <div className="text-sand-800 font-display text-[18px] leading-snug">{t.title}</div>
                      <div className="text-[11px] font-bold text-terra whitespace-nowrap shrink-0 px-2 h-6 inline-flex items-center rounded-full bg-brand-rose/30">
                        {t.ue} UE · {t.ue} FP
                      </div>
                    </div>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-sand-500 mb-3">
                      {t.audience}
                    </div>
                    <p className="text-sand-700 text-[14px] leading-relaxed flex-1">{t.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-terra text-[13px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      Im Jahres-Paket enthalten
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* REZERTIFIZIERUNGEN · hell mit Rosa-Akzent */}
        <section className="py-20 bg-cream-50 border-b border-cream-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-center gap-3 mb-3">
                <span className="h-px w-10 bg-brand-darkred" />
                <span className="text-[11px] uppercase tracking-[0.32em] font-bold text-brand-darkred">
                  Gesetzlich verpflichtend
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-[2.4rem] text-sand-800 tracking-tight leading-[1.1] mb-3">
                Rezertifizierungen<br />
                <span className="text-terra italic">jährlich, ohne Lücke.</span>
              </h2>
              <p className="text-sand-700 max-w-2xl leading-relaxed text-[15.5px]">
                Wer als Betreuungskraft oder Praxisanleitung weiter tätig sein darf, muss seine
                Qualifikation jährlich auffrischen. Wir liefern beide Programme — komplett online,
                mit Zertifikat direkt zur Personalakte.
              </p>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              {PFLICHT_REZERT.map((r, i) => (
                <Reveal key={r.slug} delay={i * 60}>
                  <div
                    data-testid={`pflicht-page-rezert-${r.slug}`}
                    className="relative rounded-3xl bg-white border border-brand-rose/50 p-7 md:p-8 overflow-hidden hover:border-terra transition-colors h-full"
                  >
                    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-brand-rose/25 blur-2xl pointer-events-none" />
                    <div className="relative">
                      <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-brand-darkred mb-2.5">
                        {r.legal} · {r.interval}
                      </div>
                      <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
                        <div className="font-display text-[1.4rem] text-sand-800 leading-tight">{r.title}</div>
                        <div className="text-[11.5px] font-bold text-terra whitespace-nowrap shrink-0 px-2.5 h-7 inline-flex items-center rounded-full bg-brand-rose/30">
                          {r.ue} UE / Jahr
                        </div>
                      </div>
                      <p className="text-sand-700 text-[14.5px] leading-relaxed">{r.short}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Footer · Rosa-Hero-Band */}
        <section className="relative py-24 md:py-36 bg-brand-rose/25 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-brand-rose/30 blur-3xl" />
            <div className="absolute -bottom-32 right-1/4 w-[32rem] h-[32rem] rounded-full bg-peach-200/40 blur-3xl" />
          </div>
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Reveal>
              <div className="text-[11px] uppercase tracking-[0.32em] font-bold text-brand-darkred mb-3">
                Wir bauen euch den Jahresplan
              </div>
              <h2 className="font-display text-sand-800 text-3xl sm:text-[2.5rem] font-semibold tracking-tight leading-[1.1]">
                Sag uns dein Team. <span className="text-terra italic">Wir machen den Rest.</span>
              </h2>
              <p className="mt-5 text-sand-700 text-[16px] leading-[1.7]">
                Wie groß ist dein Team? Welche Qualifikationen müssen abgedeckt werden?
                Wir kombinieren die passenden Pflichtthemen, schlagen Termine vor und liefern die
                Zertifikate direkt für deine MD-Akte.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  asChild
                  data-testid="pflicht-page-footer-cta"
                  className="rounded-full bg-terra hover:bg-terra-dark text-white h-12 px-7 text-[14.5px] font-semibold"
                >
                  <Link to="/#kontakt">
                    Jetzt Beratung anfragen
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.8} />
                  </Link>
                </Button>
                <a
                  href="tel:+4921129273330"
                  className="inline-flex items-center gap-2 text-[14.5px] font-semibold text-brand-darkred underline-offset-4 hover:underline"
                >
                  <Phone className="h-4 w-4" strokeWidth={1.8} />
                  0211 29273330
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
